import React, { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { supabase, Tables } from "../utils/supabase";
import {
  BellIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChatBubbleLeftRightIcon,
} from "@heroicons/react/24/outline";
import { formatDistanceToNow } from "date-fns";
import { nl } from "date-fns/locale";

interface NotificationItem {
  id: string;
  type: "unread_comm" | "overdue_task";
  title: string;
  subtitle: string;
  timestamp: string;
  page: string;
}

interface NotificationDropdownProps {
  onNavigate: (page: string) => void;
  unreadCount: number;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  onNavigate,
  unreadCount,
}) => {
  const { user, selectedPharmacy } = useAuth();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handleOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  // Fetch when opened
  useEffect(() => {
    if (!open || !selectedPharmacy || !user) return;
    let cancelled = false;

    const fetch = async () => {
      setLoading(true);
      try {
        const [commsRes, readsRes, tasksRes] = await Promise.all([
          supabase
            .from(Tables.COMMUNICATIONS)
            .select("id, title, created_at")
            .eq("pharmacy_id", selectedPharmacy.id)
            .order("created_at", { ascending: false })
            .limit(20),
          supabase
            .from(Tables.COMMUNICATION_READS)
            .select("communication_id")
            .eq("user_id", user.id),
          supabase
            .from(Tables.TASKS)
            .select("id, title, due_date")
            .eq("pharmacy_id", selectedPharmacy.id)
            .in("status", ["pending", "in_progress"])
            .lt("due_date", new Date().toISOString())
            .order("due_date", { ascending: true })
            .limit(5),
        ]);

        if (cancelled) return;

        const readIds = new Set(
          (readsRes.data || []).map((r) => r.communication_id),
        );
        const unreadComms: NotificationItem[] = (commsRes.data || [])
          .filter((c) => !readIds.has(c.id))
          .slice(0, 5)
          .map((c) => ({
            id: c.id,
            type: "unread_comm" as const,
            title: c.title,
            subtitle: "Ongelezen bericht",
            timestamp: c.created_at,
            page: "communications",
          }));

        const overdueTasks: NotificationItem[] = (tasksRes.data || []).map(
          (t) => ({
            id: t.id,
            type: "overdue_task" as const,
            title: t.title,
            subtitle: `Vervallen op ${new Date(t.due_date).toLocaleDateString("nl-NL")}`,
            timestamp: t.due_date,
            page: "tasks",
          }),
        );

        setItems([...overdueTasks, ...unreadComms]);
      } catch {
        // silently fail
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetch();
    return () => {
      cancelled = true;
    };
  }, [open, selectedPharmacy?.id, user?.id]);

  const handleNavigate = (page: string) => {
    onNavigate(page);
    setOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="p-1 rounded-full text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        title="Meldingen"
      >
        <BellIcon className="h-6 w-6" />
      </button>
      {unreadCount > 0 && (
        <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-[1rem] h-4 px-0.5 text-xs font-bold text-white bg-red-500 rounded-full pointer-events-none">
          {unreadCount > 9 ? "9+" : unreadCount}
        </span>
      )}

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5 z-50">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900">Meldingen</h3>
            <button
              onClick={() => setOpen(false)}
              className="text-gray-400 hover:text-gray-500"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>

          {loading ? (
            <div className="px-4 py-6 text-center text-sm text-gray-500">
              Laden…
            </div>
          ) : items.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <CheckCircleIcon className="h-8 w-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Geen nieuwe meldingen</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-100 max-h-80 overflow-y-auto">
              {items.map((item) => {
                const Icon =
                  item.type === "overdue_task"
                    ? ExclamationTriangleIcon
                    : ChatBubbleLeftRightIcon;
                return (
                  <li key={`${item.type}-${item.id}`}>
                    <button
                      className="w-full flex items-start px-4 py-3 hover:bg-gray-50 text-left transition-colors"
                      onClick={() => handleNavigate(item.page)}
                    >
                      <Icon
                        className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
                          item.type === "overdue_task"
                            ? "text-red-500"
                            : "text-primary-500"
                        }`}
                      />
                      <div className="ml-3 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {item.title}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {item.subtitle}
                        </p>
                        <p className="text-xs text-gray-400 mt-0.5">
                          {formatDistanceToNow(new Date(item.timestamp), {
                            addSuffix: true,
                            locale: nl,
                          })}
                        </p>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}

          <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 rounded-b-lg flex justify-between items-center">
            <button
              onClick={() => handleNavigate("communications")}
              className="text-xs text-primary-600 hover:text-primary-700 font-medium"
            >
              Alle communicatie →
            </button>
            <button
              onClick={() => {
                sessionStorage.setItem(
                  "task_filter_hint",
                  JSON.stringify({ status: "overdue" }),
                );
                handleNavigate("tasks");
              }}
              className="text-xs text-red-600 hover:text-red-700 font-medium"
            >
              Verlopen taken →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
